import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, BookOpen, LifeBuoy, Clock, ChevronDown, ChevronRight, ChevronLeft } from 'lucide-react';

const LEVELS = [
  { id: 'beginner', label: 'Beginner' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
];

function slugify(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function buildSearchText(article) {
  const parts = [
    article.title,
    article.summary,
    article.level,
    article.conclusion,
    ...(article.tags || []),
    ...(article.key_points || []),
    ...(article.further_reading || []),
  ];
  (article.sections || []).forEach((section) => {
    parts.push(section.heading);
    (section.body || []).forEach((block) => {
      Object.values(block).forEach((text) => parts.push(text));
    });
  });
  (article.glossary || []).forEach(({ term, definition }) => {
    parts.push(term, definition);
  });
  return parts.filter(Boolean).join(' ').toLowerCase();
}

function extractParagraphs(block) {
  if (!block || typeof block !== 'object') return [];
  return Object.keys(block)
    .filter((key) => key.startsWith('paragraph'))
    .sort((a, b) => {
      const numA = parseInt(a.replace('paragraph', ''), 10) || 0;
      const numB = parseInt(b.replace('paragraph', ''), 10) || 0;
      return numA - numB;
    })
    .map((key) => block[key])
    .filter(Boolean);
}

function LevelBadge({ level }) {
  const label = level.charAt(0).toUpperCase() + level.slice(1);
  return <span className={`kc-level-badge kc-level-badge--${level}`}>{label}</span>;
}

function ArticleContent({ article }) {
  return (
    <article className="kc-article">
      <div className="kc-article-header">
        <div className="kc-article-meta">
          <LevelBadge level={article.level} />
          {article.estimated_read_minutes != null && (
            <span className="kc-read-time">
              <Clock size={14} aria-hidden />
              {article.estimated_read_minutes} min read
            </span>
          )}
        </div>
        {article.summary && <p className="kc-article-summary">{article.summary}</p>}
        {article.tags?.length > 0 && (
          <div className="kc-tag-list" aria-label="Tags">
            {article.tags.map((tag) => (
              <span key={tag} className="kc-tag">{tag}</span>
            ))}
          </div>
        )}
      </div>

      {(article.sections || []).map((section) => (
        <section key={section.heading} className="kc-article-section">
          <h3 className="kc-section-heading">{section.heading}</h3>
          {(section.body || []).map((block, idx) => (
            <div key={idx} className="kc-section-body">
              {extractParagraphs(block).map((paragraph, pIdx) => (
                <p key={pIdx}>{paragraph}</p>
              ))}
            </div>
          ))}
        </section>
      ))}

      {article.key_points?.length > 0 && (
        <section className="kc-key-points" aria-labelledby="kc-key-points-title">
          <h3 id="kc-key-points-title" className="kc-section-heading">Key Points</h3>
          <ul className="kc-key-points-list">
            {article.key_points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </section>
      )}

      {article.conclusion && (
        <section className="kc-conclusion" aria-labelledby="kc-conclusion-title">
          <h3 id="kc-conclusion-title" className="kc-section-heading">Summary</h3>
          <p>{article.conclusion}</p>
        </section>
      )}

      {article.glossary?.length > 0 && (
        <section className="kc-glossary" aria-labelledby="kc-glossary-title">
          <h3 id="kc-glossary-title" className="kc-section-heading">Glossary</h3>
          <dl className="kc-glossary-list">
            {article.glossary.map(({ term, definition }) => (
              <div key={term} className="kc-glossary-item">
                <dt>{term}</dt>
                <dd>{definition}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {article.further_reading?.length > 0 && (
        <section className="kc-further-reading" aria-labelledby="kc-further-title">
          <h3 id="kc-further-title" className="kc-section-heading">Further reading</h3>
          <ul className="kc-further-list">
            {article.further_reading.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

const Learn = () => {
  const [query, setQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activeArticleId, setActiveArticleId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedLevels, setExpandedLevels] = useState(() =>
    Object.fromEntries(LEVELS.map(({ id }) => [id, false]))
  );

  useEffect(() => {
    let cancelled = false;
    fetch('/api/learning-content')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load articles');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const enriched = (data.articles || []).map((article) => ({
          ...article,
          id: slugify(article.title),
          searchText: buildSearchText(article),
        }));
        setArticles(enriched);
        if (enriched.length > 0) {
          setActiveArticleId(enriched[0].id);
        }
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || 'Failed to load content');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const filteredArticles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return articles;
    return articles.filter(
      (article) =>
        article.title.toLowerCase().includes(q) ||
        article.searchText.includes(q)
    );
  }, [articles, query]);

  const articlesByLevel = useMemo(() => {
    const grouped = Object.fromEntries(LEVELS.map(({ id }) => [id, []]));
    filteredArticles.forEach((article) => {
      if (grouped[article.level]) {
        grouped[article.level].push(article);
      }
    });
    return grouped;
  }, [filteredArticles]);

  useEffect(() => {
    if (filteredArticles.length === 0) return;
    const ids = filteredArticles.map((a) => a.id);
    if (!ids.includes(activeArticleId)) {
      setActiveArticleId(ids[0]);
    }
  }, [filteredArticles, activeArticleId]);

  const activeArticle = useMemo(
    () => articles.find((a) => a.id === activeArticleId),
    [articles, activeArticleId]
  );

  const toggleLevel = useCallback((levelId) => {
    setExpandedLevels((prev) => ({ ...prev, [levelId]: !prev[levelId] }));
  }, []);

  const hasQuery = query.trim().length > 0;
  const noResults = hasQuery && filteredArticles.length === 0;

  const isLevelExpanded = useCallback(
    (levelId) => {
      if (hasQuery && (articlesByLevel[levelId]?.length ?? 0) > 0) return true;
      return expandedLevels[levelId];
    },
    [hasQuery, articlesByLevel, expandedLevels]
  );

  return (
    <div className="page-content info-page help-page">
      <header className="info-page-header info-page-header--actions">
        <div className="kc-header-text">
          <h2>
            <BookOpen size={22} className="info-page-header-icon" aria-hidden />
            Knowledge Center
          </h2>
          <p className="kc-header-subtitle">
            Investing guides from beginner basics to advanced strategies
          </p>
        </div>
        <div className="info-page-header-actions">
          <Link to="/support" className="pw-btn pw-btn-primary">
            <LifeBuoy size={16} aria-hidden />
            Contact Support
          </Link>
        </div>
      </header>

      <div className="help-search-bar">
        <label htmlFor="kc-search" className="help-search-label">
          Search topics
        </label>
        <div className="help-search-wrap pw-search-wrap help-search-wrap--wide">
          <Search className="pw-search-icon" size={16} aria-hidden />
          <input
            id="kc-search"
            type="search"
            className="pw-search-input help-search-input--wide"
            placeholder="e.g. options, risk management, P/E ratio, backtesting..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            aria-describedby={hasQuery ? 'kc-search-results' : undefined}
          />
          {query && (
            <button
              type="button"
              className="pw-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {hasQuery && (
          <p id="kc-search-results" className="help-search-results" aria-live="polite">
            {noResults
              ? 'No matching topics. Try different keywords (e.g. diversification, volatility, taxes).'
              : `${filteredArticles.length} ${filteredArticles.length === 1 ? 'topic' : 'topics'} found`}
          </p>
        )}
      </div>

      {loading ? (
        <div className="pw-loading">
          <div className="pw-spinner" aria-hidden />
          <p>Loading articles…</p>
        </div>
      ) : loadError ? (
        <div className="help-no-results">
          <p>{loadError}</p>
          <p>Please refresh the page or <Link to="/support">contact support</Link> if the problem persists.</p>
        </div>
      ) : noResults ? (
        <div className="help-no-results">
          <p>We couldn&apos;t find any topics matching &quot;<strong>{query.trim()}</strong>&quot;.</p>
          <p>Try searching for: <strong>diversification</strong>, <strong>options</strong>, <strong>risk management</strong>, <strong>technical analysis</strong>, or <strong>portfolio</strong>.</p>
        </div>
      ) : (
        <div className={`help-layout kc-layout ${sidebarOpen ? '' : 'kc-layout--sidebar-collapsed'}`}>
          <nav
            className={`help-sidebar kc-sidebar ${sidebarOpen ? '' : 'kc-sidebar--collapsed'}`}
            aria-label="Knowledge topics"
          >
            <div className="kc-sidebar-inner">
              <div className="kc-sidebar-header">
                {sidebarOpen ? (
                  <p className="kc-sidebar-heading">Topics</p>
                ) : (
                  <span className="kc-sidebar-label-vertical" aria-hidden>Topics</span>
                )}
                <button
                  type="button"
                  className="kc-sidebar-toggle"
                  onClick={() => setSidebarOpen((open) => !open)}
                  aria-expanded={sidebarOpen}
                  aria-label={sidebarOpen ? 'Collapse topics menu' : 'Expand topics menu'}
                >
                  {sidebarOpen ? <ChevronLeft size={18} aria-hidden /> : <ChevronRight size={18} aria-hidden />}
                </button>
              </div>
              {sidebarOpen && LEVELS.map(({ id, label }) => {
                const levelArticles = articlesByLevel[id] || [];
                if (levelArticles.length === 0) return null;
                const isExpanded = isLevelExpanded(id);
                return (
                  <div key={id} className="kc-level-group">
                    <button
                      type="button"
                      className="kc-level-toggle"
                      onClick={() => toggleLevel(id)}
                      aria-expanded={isExpanded}
                      aria-controls={`kc-level-${id}`}
                    >
                      {isExpanded ? (
                        <ChevronDown size={16} aria-hidden />
                      ) : (
                        <ChevronRight size={16} aria-hidden />
                      )}
                      <span>{label}</span>
                      <span className="kc-level-count">{levelArticles.length}</span>
                    </button>
                    {isExpanded && (
                      <div id={`kc-level-${id}`} className="kc-level-topics" role="group" aria-label={`${label} topics`}>
                        {levelArticles.map((article) => (
                          <button
                            key={article.id}
                            type="button"
                            className={`help-nav-item kc-topic-item ${activeArticleId === article.id ? 'active' : ''}`}
                            onClick={() => setActiveArticleId(article.id)}
                            aria-current={activeArticleId === article.id ? 'true' : undefined}
                          >
                            {article.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          <main className="help-content info-data-card kc-content" id="kc-article">
            {activeArticle && (
              <section className="help-section kc-article-wrap" aria-labelledby="kc-article-title">
                <h2 id="kc-article-title" className="help-section-title">{activeArticle.title}</h2>
                <div className="help-section-body">
                  <ArticleContent article={activeArticle} />
                </div>
              </section>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default Learn;
